import styled, { css } from 'styled-components';
import { Typography } from '../../../../global/theme';
import { mobile_xl } from '../../../../global/mediaQueryMixin';
import { LineClamp } from '../../../../global/commonStyle';

interface Props {
  isReaction?: boolean;
  isActive?: boolean;
  showGap?: boolean;
  isReply?: boolean;
  isProjectMessageDetail?: boolean;
  isFirstUserMessage?: boolean;
  isLastUserMessage?: boolean;
  hasUserImage?: boolean;
}

const Comments = styled.div<Props>`
  display: flex;
  gap: var(--space-16);
  padding-top: ${({ isFirstUserMessage }) => (isFirstUserMessage ? 'var(--space-8)' : 'var(--space-4)')};
  padding-bottom: ${({ isLastUserMessage, isReaction }) =>
    isLastUserMessage || isReaction ? 'var(--space-18)' : 'var(--space-0)'};
  position: relative;
  ${mobile_xl(css<Props>`
    gap: var(--space-8);
    width: calc(100vw - 40px);
    margin: var(--space-0) auto;
    padding-top: ${({ isFirstUserMessage }) => (isFirstUserMessage ? 'var(--space-12)' : 'var(--space-4)')};
  `)}
`;

const LeftComment = styled.div<Props>`
  margin-left: ${({ hasUserImage }) => (hasUserImage ? 'var(--space-0)' : 'var(--space-36)')};
  display: flex;
  flex-direction: column-reverse;
  ${(props: Props) =>
    props.isProjectMessageDetail &&
    css`
      margin-top: -2px;
    `}
  ${mobile_xl(css<Props>`
    margin-left: ${(props: Props) => (props.hasUserImage ? 'var(--space-0)' : 'var(--space-32)')};
    .avatar {
      width: 32px;
      height: 32px;
      span {
        font-size: 12px;
      }
    }
  `)}
`;

const RightComment = styled.div<Props>`
  display: flex;
  flex-direction: column;
  position: relative;
  .reaction-container {
    max-width: fit-content;
    position: absolute;
    bottom: -20px;
  }
  &:hover {
    .right-section {
      display: block;
    }
  }

  ${(props: Props) =>
    props.isActive &&
    css`
      .right-section {
        display: block;
      }
    `}
`;

const Time = styled.p`
  ${Typography.body_sm}
  color: var(--text-secondary);
  margin: var(--space-0);
`;

const TagDesc = styled.div<Props>`
  padding: var(--space-8) var(--space-12);
  background-color: var(--background-active-navbar);
  border-radius: var(--border-radius-4) var(--border-radius-12) var(--border-radius-12) var(--border-radius-4);
  max-width: 500px;
  width: max-content;
  @media (max-width: 1140px) {
    max-width: calc(100vw - 640px);
  }

  ${mobile_xl(css`
    padding: var(--space-8);
    gap: var(--space-4);
    max-width: calc(100vw - 96px);
  `)}
  &.tiptap > .description {
    overflow: auto;
    .tableWrapper {
      background: var(--background-tertiary);
      border: 0.5px solid var(--border-secondary);
      border-radius: var(--border-radius-8);
      scrollbar-color: var(--text-placeholder) transparent;
      scrollbar-width: thin;
    }
  }
  &.tiptap > .description > div > div:first-child {
    margin-top: var(--space-4) !important;
  }
  &.tiptap > .description > div > div:last-child {
    margin-bottom: var(--space-4) !important;
  }
  &.tiptap .description {
    margin-top: var(--space-0) !important;
  }
  h1,
  h2,
  h3,
  h4,
  h5,
  h6 {
    color: var(--text-primary);
  }
  .prosemirror-mention-node {
    color: var(--brand-primary) !important;
  }
  .figma-filename {
    ${LineClamp(1)}
    text-overflow: ellipsis;
    max-width: 80px;
  }
  .figma-description {
    ${LineClamp(1)}
    text-overflow: ellipsis;
    max-width: 173px;
  }
  p {
    color: var(--text-primary);
  }
  ul,
  ol {
    color: var(--text-primary);
    li {
      ::marker {
        color: var(--text-primary);
        background-color: transparent;
      }
    }
  }
  p > span,
  strong {
    color: var(--text-primary);
  }
  display: flex;
  flex-wrap: wrap;
  ${(props) =>
    props.showGap &&
    css`
      gap: var(--space-8);
      flex-direction: column;
    `}
  ${(props) =>
    props.isReply &&
    css`
      gap: var(--space-8);
      flex-direction: column;
    `}
    ${(props) =>
    props.isFirstUserMessage &&
    css`
      border-top-left-radius: 12px;
    `}
    ${(props) =>
    props.isLastUserMessage &&
    css`
      border-bottom-left-radius: 12px;
    `}
  img {
    .img {
      max-width: 50%;
      max-height: 50%;
    }
  }
  .thread-div {
    cursor: pointer;
    padding: var(--space-8);
    border-radius: var(--border-radius-8);
    border: 0.5px solid var(--border-primary);
    background: var(--background-tertiary);
    margin-top: var(--space-4);
    max-width: -webkit-fill-available;
  }
  .content {
    display: flex;
    flex-direction: row;
    gap: var(--space-10);
  }
  .line {
    min-width: 1px;
    background-color: var(--text-placeholder);
    border: 0.5px solid var(--text-placeholder);
    height: auto;
    border-radius: var(--border-radius-4);
  }
  .right-div {
    display: flex;
    flex-direction: column;
    gap: var(--space-4);
    overflow: auto;

    .tableWrapper {
      overflow: auto;
      scrollbar-color: var(--text-placeholder) transparent;
      scrollbar-width: thin;
      table {
        border-radius: var(--border-radius-0);
        border: unset;
      }
    }
  }

  .header {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
  }
  .left-header {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: var(--space-4);
  }
  .name {
    color: var(--text-primary);
    ${Typography.body_md_semibold}
  }
  .time {
    color: var(--text-secondary);
    ${Typography.body_md}
  }

  .file-div {
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: var(--space-5);
    flex-wrap: wrap;
  }
  .image-div {
    max-width: 240px;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: var(--space-4);
    svg {
      min-width: 32px;
      min-height: 32px;
    }
  }
  .filetext {
    color: var(--text-primary);
    ${Typography.body_md_medium}
    margin-top: var(--space-2);
  }

  ${mobile_xl(css`
    .thread-div {
      margin-top: var(--space-0);
      padding: var(--space-6) var(--space-8);
    }
    .filetext {
      ${LineClamp(1)}
    }
    .figma-description {
      max-width: 243px;
    }
    .figma-filename {
      max-width: 140px;
    }
  `)}
`;

const Desc = styled.div`
  ${Typography.body_md_medium}
  color: var(--text-primary);
  margin: var(--space-0);
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
  word-break: break-word;
  a {
    color: var(--text-link);
    word-break: break-word;
    text-decoration: underline;
    &:hover {
      text-decoration: none;
    }
  }
  .geist-ellipsis {
    white-space: normal;
  }
  .file-box {
    max-width: max-content;
    display: inline-flex;
    padding: var(--space-10) var(--space-16);
    background-color: var(--background-primary);
    border: 1px solid var(--border-primary);
    border-radius: var(--border-radius-8);
    box-shadow: var(--shadow-card);
    align-items: center;
    gap: var(--space-16);
  }
  .textarea {
    width: 130px;
    white-space: unset;
  }
  .h4 {
    ${Typography.body_md_medium}
    color: var(--text-primary);
    margin: var(--space-0) var(--space-0) var(--space-4);
    ${LineClamp(1)}
    white-space: inherit;
  }
  p {
    ${Typography.body_md}
    color: var(--text-primary);
  }
  .svg {
    width: 32px;
    height: 32px;
  }

  .e-rte-table td,
  .e-rte-table th {
    border: 1px solid var(--border-primary);
  }
`;

const OverLay = styled.div`
  position: absolute;
  border-radius: var(--border-radius-full);
  background-color: var(--background-overlay-2);
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  display: none;
`;

const AvatarWrap = styled.div`
  cursor: pointer;
  position: relative;
  &:hover {
    ${OverLay} {
      display: block;
    }
  }
`;

const CommentSection = styled.div``;

export { Comments, Desc, LeftComment, RightComment, TagDesc, Time, AvatarWrap, OverLay, CommentSection };
